import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CameraState, Size, XyzDataPoint } from '../types';
import { niceAxis } from '@ardoq/math';
import { hasColorField, rangeOf, toColorRange } from '../data/ranges';
import { toTickInset, type HorizontalAxes } from '../data/horizontalAxes';
import { DEFAULT_CAMERA } from '../geometry/projection';
import { useOrbitInteraction } from '../interaction/useOrbitInteraction';
import { clampZoom } from '../interaction/orbitCamera';
import { CHROME_INSET, fitZoom } from '../geometry/fitZoom';
import {
  createColorScale,
  type SequentialRampName,
} from '../render/colorScale';
import { renderAxes3d } from '../render/renderAxes3d';
import {
  AXIS_BOX_THICKNESS,
  axisBoxBase,
  axisBoxBounds,
} from '../render/axisBox';
import { renderAxisBox, renderCrosshair3d } from '../render/renderAxisBox';
import {
  DEFAULT_POINTS_STYLE,
  findMarkInCache,
  hasSizeField,
  refreshMarkHitCache,
  renderPoints3d,
  toMarkUnitPoint,
  type MarkHitCache,
  type Points3dStyle,
} from '../render/renderPoints3d';
import { tooltipFlipTransform } from './tooltipPlacement';
import {
  resolvePointsLook,
  type PointsLookOverrides,
} from '../render/pointsLook';
import { toExportCanvas } from './exportCanvas';
import { useChartCanvas, useContainerSize } from './useChartCanvas';
import { MarkList, type MarkListColors } from '../interaction/MarkList';
import { useMarkFocus } from '../interaction/useMarkFocus';
import { toPointHighlights, toPointListItems } from './pointMarkItems';
import { NUMBER_AXIS_SCALES, toAxisLabels } from '../data/axisLabels';

export type Points3DChartProps = {
  points: XyzDataPoint[];
  xTitle: string;
  yTitle: string;
  zTitle: string;
  /**
   * The fields on the two channels that have no axis, for the accessible name.
   *
   * Optional because either channel may be unused - a scatter varies no area, and a chart with no
   * colour field colours by component type. Named rather than inferred, because the chart is handed
   * numbers and cannot know what they measure, and this is currently the only place in the package
   * the mapping from field to channel is written down at all.
   */
  radiusTitle?: string;
  /**
   * The whole accessible name, assembled by the host.
   *
   * Threaded in because this package has no locale, and it carries more than the others: with no
   * legend, this sentence is the only place the app says what mark area or colour means. Given the
   * channels the picture is actually carrying, so a host cannot announce one that is not there.
   */
  formatAccessibleName: (parts: AccessibleNameParts) => string;
  /**
   * One clause per channel, worded by the host.
   *
   * A `Record` over the whole union rather than an object of optional fields: every kind must have
   * a clause, so adding one to `ChannelKind` stops the build at each host until it does. That is the
   * difference between this and a formatter taking a bag of titles, which accepts an extra field
   * silently and announces nothing.
   */
  channelLabels: Record<ChannelKind, (field: string) => string>;
  colorTitle?: string;
  /**
   * The two horizontal axes, decided by the host.
   *
   * Required rather than derived from `points`, so the four XYZ views can be made to agree about
   * the plane they share and so the choice sits in the source-data layer with the rest of the
   * derivation. `toDataHorizontalAxes` is the baseline for a caller with no opinion.
   *
   * The range must contain the points' own extent. A narrower one does not clip - it places marks
   * outside the box.
   */
  axes: HorizontalAxes;
  ramp: SequentialRampName;
  bands: number;
  style?: Partial<Points3dStyle>;
  /** How the marks are lit, outlined and grounded. Omitted, they look as they always have. */
  look?: PointsLookOverrides;
  camera?: CameraState;
  format: (value: number) => string;
  /**
   * Publishes the chart's export hook once there is a canvas, and null when it goes.
   *
   * A callback rather than a ref the host fills, so the host cannot hold a hook that outlives the
   * canvas it reads. Optional: the stories and the tests have nothing to export to.
   */
  onExportCanvasAvailable?: (
    createExportCanvas: (() => OffscreenCanvas | null) | null
  ) => void;
  /**
   * The header lines the export has to carry, since the exported file is the canvas alone.
   *
   * Passed in rather than built here: they are the host's own DOM header, translated, and this
   * package has no locale.
   */
  exportCaptions?: readonly string[];
  surfaceColor: string;
  inkColor: string;
  gridColor: string;
  /**
   * The axis box's faces.
   *
   * Required rather than defaulted, because the box is always drawn and a wall the wrong side of
   * the surface behind it is worse than no wall. `surfaceDefaultSubtle` is the token: which
   * direction "one step off the surface" goes belongs to the theme.
   */
  panelColor: string;
  /** Fired by a click on a mark, and by Enter or Space on its row in the mark list. */
  onPointClick?: (point: XyzDataPoint) => void;
  /**
   * The accessible name and heading of the list of plotted components that follows the canvas in
   * the tab order, worded by the host.
   */
  markListLabel: string;
  /**
   * Shown instead of the chart when there is nothing to plot.
   *
   * Needed here more than on the other two, because this is the state these views open in: no
   * fields are preselected, and without it an empty chart draws a fully labelled 0-to-1 axis cube
   * - `rangeOf` answers [0, 1] for an empty array - so a view configured against an annual-cost
   * field announces values from 0 to 1 that no component has.
   */
  emptyState?: React.ReactNode;
};

/** Pixels a press may travel and still count as a click rather than the tail of a rotation. */
const CLICK_TRAVEL_LIMIT = 4;

type ChartDescription = {
  pointCount: number;
  /** Which of the two charts this is, which names it. Not whether the size channel is in use. */
  isBubble: boolean;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  radiusTitle: string | undefined;
  colorTitle: string | undefined;
  hasSizeField: boolean;
  hasColorField: boolean;
};

/**
 * What a screen reader hears on the chart canvas.
 *
 * Names the fields rather than only the chart type, because on a chart whose axes are chosen per
 * view "scatter chart" identifies nothing, and mentions the keyboard path because nothing else on
 * the element hints that it is more than a picture.
 */
/**
 * The channels the picture is actually carrying, for the host to word.
 *
 * Which channels those are stays here: a bubble whose size field is empty on every plotted
 * component draws uniform marks, and a colour field nothing reads is not an encoding - both are
 * questions about the data, which the host does not have. `null` means "not in use", so a caller
 * cannot accidentally announce a channel the chart is not showing.
 */
export type ChannelKind = 'across' | 'deep' | 'height' | 'size' | 'color';

/** Reading order, and the only place it is decided. */
const CHANNEL_ORDER: ChannelKind[] = [
  'across',
  'deep',
  'height',
  'size',
  'color',
];

type AccessibleNameParts = {
  pointCount: number;
  isBubble: boolean;
  /** The clauses for the channels in use, already worded by the host, in reading order. */
  channels: string[];
};

/**
 * Which channels the picture is carrying, worded by the host.
 *
 * The decision stays here because it is about the data: a bubble whose size field is empty on every
 * plotted component draws uniform marks, and a colour field nothing reads is not an encoding. The
 * words come from `channelLabels`, a `Record` over every `ChannelKind` rather than a function taking
 * a bag of fields - so adding a channel fails the build at both hosts, instead of compiling into a
 * chart that announces it nowhere, which is how the size field went unnamed until it was noticed.
 */
const toChannels = (
  {
    isBubble,
    xTitle,
    yTitle,
    zTitle,
    radiusTitle,
    colorTitle,
    hasSizeField,
    hasColorField,
  }: ChartDescription,
  channelLabels: Record<ChannelKind, (field: string) => string>
): string[] => {
  const fields: Record<ChannelKind, string | null> = {
    across: xTitle,
    deep: yTitle,
    height: zTitle,
    /* From the data rather than from the variant, the way the colour channel already reads its own
       question - and the way `renderPoints3d` was corrected to. */
    size: isBubble && hasSizeField && radiusTitle ? radiusTitle : null,
    color: hasColorField && colorTitle ? colorTitle : null,
  };

  return CHANNEL_ORDER.flatMap(kind => {
    const field = fields[kind];
    return field === null ? [] : [channelLabels[kind](field)];
  });
};

/** The camera at the zoom that puts the whole axis box on screen. */
const toFittedCamera = (camera: CameraState, viewport: Size): CameraState => ({
  ...camera,
  zoom: clampZoom(
    camera.zoom *
      fitZoom({
        camera: { ...camera, zoom: 1 },
        viewport,
        bounds: axisBoxBounds(camera.heightScale),
        inset: CHROME_INSET,
      })
  ),
});

/**
 * Scatter and bubble in one component: the only difference between them is whether the
 * radius field varies mark area, which is a style flag rather than a different chart.
 */
export const Points3DChart = ({
  points,
  xTitle,
  yTitle,
  zTitle,
  radiusTitle,
  colorTitle,
  formatAccessibleName,
  channelLabels,
  onExportCanvasAvailable,
  exportCaptions,
  axes,
  ramp,
  bands,
  style,
  look,
  camera,
  format,
  surfaceColor,
  panelColor,
  inkColor,
  gridColor,
  onPointClick,
  markListLabel,
  emptyState,
}: Points3DChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** Where the press started, so a rotation can be told from a selection. */
  const pressedAt = useRef<{ x: number; y: number } | null>(null);
  const { containerRef, viewport } = useContainerSize();

  /** Whether the camera is the reader's now, or still the fit's. */
  const isCameraOwnedByReader = useRef(false);
  const fittedCameraRef = useRef<CameraState | null>(null);
  const fittedCamera = useMemo(() => {
    const held = fittedCameraRef.current;
    if (held && isCameraOwnedByReader.current) return held;

    const fitted = toFittedCamera(camera ?? DEFAULT_CAMERA, viewport);
    fittedCameraRef.current = fitted;
    return fitted;
  }, [camera, viewport]);

  const {
    orbit,
    isRotating,
    isTurning,
    hasReaderMovedCamera,
    bindCanvas,
    canvasProps,
    toLocalPoint,
  } = useOrbitInteraction(canvasRef, fittedCamera);

  if (hasReaderMovedCamera) isCameraOwnedByReader.current = true;
  const [hovered, setHovered] = useState<XyzDataPoint | null>(null);
  /**
   * Where the cursor was last seen on the canvas, or null once it has left.
   *
   * Kept so a camera change with no pointer event behind it still has somewhere to re-resolve
   * against. `pointerup` has the release position and uses it; an arrow key, a zoom key and the
   * wheel have none, and all three move the marks under a cursor that never moved.
   */
  const lastCursor = useRef<{ x: number; y: number } | null>(null);
  /**
   * Where to put the tooltip, in canvas coordinates.
   *
   * State rather than the ref above, because the tooltip is DOM: a ref updates without re-rendering,
   * so the box would sit wherever the pointer first resolved a mark and stay there. Set from the
   * same events that set `hovered`, so the two cannot disagree about which mark is being described.
   */
  const [tooltipAt, setTooltipAt] = useState<{ x: number; y: number } | null>(
    null
  );
  /**
   * What the hover was last resolved against, so an unrelated render costs no scan.
   *
   * State rather than a ref, because the resolve below runs *during* the render and queues
   * `setHovered` alongside this. A ref would be written by a pass React then discarded - an
   * interrupted concurrent render, which the native wheel listener's default-priority dispatch is
   * exactly the shape to produce - leaving the marker claimed and the hover update gone, so the
   * guard would decline on the restart and the stale mark would survive until the pointer moved.
   * Both are state, so both commit or neither does.
   */
  const [resolvedAt, setResolvedAt] = useState<{
    camera: CameraState;
    viewport: Size;
    style: {
      showSizeVariation: boolean;
      maximumDiameter: number;
      opacity: number;
    };
  } | null>(null);

  /* Dropped when the dataset changes. A hover naming a component that is no longer plotted matches
     no mark, and `renderPoints3d` dims every mark that is not the highlighted one - so the whole
     chart repaints at 15% alpha and stays there until the pointer moves. Reachable without any
     pointer movement at all: a filter applied elsewhere, a live refresh, an undo. */
  /* The cursor goes with the hover. An empty-to-non-empty transition unmounts and remounts the
     canvas rather than firing `pointerleave`, so a position recorded before the data arrived
     survived into the first frame with marks in it - and the re-resolve below would invent a hover
     at a point the cursor had left, which is the failure the controller's own comment describes. */
  useEffect(() => {
    lastCursor.current = null;
    setHovered(null);
    setTooltipAt(null);
  }, [points]);

  /* Destructured to numbers, then rebuilt, for the same reason `style` and `margin` are: `axes`
     is a host-built object, and a host that assembles it inline hands over a new one on every
     parent render. Fed straight into `paint`'s dependency list that reads as a change, and every
     such render repaints - which here is the per-device-pixel band loop. */
  const {
    x: {
      range: xAxisRange,
      tickCount: xTickCount,
      padding: [xPaddingLow, xPaddingHigh],
    },
    y: {
      range: yAxisRange,
      tickCount: yTickCount,
      padding: [yPaddingLow, yPaddingHigh],
    },
  } = axes;
  const [xAxisMin, xAxisMax] = xAxisRange;
  const [yAxisMin, yAxisMax] = yAxisRange;
  const resolvedAxes = useMemo<HorizontalAxes>(
    () => ({
      x: {
        range: [xAxisMin, xAxisMax],
        tickCount: xTickCount,
        padding: [xPaddingLow, xPaddingHigh],
      },
      y: {
        range: [yAxisMin, yAxisMax],
        tickCount: yTickCount,
        padding: [yPaddingLow, yPaddingHigh],
      },
    }),
    [
      xAxisMin,
      xAxisMax,
      xTickCount,
      xPaddingLow,
      xPaddingHigh,
      yAxisMin,
      yAxisMax,
      yTickCount,
      yPaddingLow,
      yPaddingHigh,
    ]
  );

  /**
   * The height axis, rounded to the numbers a reader would name.
   *
   * Derived here rather than handed in like the horizontals, because it is a fact about what this
   * chart plots rather than about the plane the four views share - and on the gridded charts it
   * spans the interpolated values, which do not exist until a lattice is built. See
   * `HorizontalAxes`.
   *
   * A dataset with no spread in z passes through unrounded on four divisions.
   */
  const heightAxis = useMemo(
    () => niceAxis(rangeOf(points, ({ z }) => z)),
    [points]
  );

  /* One object for every consumer, so the marks, the axes, the box and the hit test are all
     measured against the same ranges. The horizontals are the host's, read straight off `axes`:
     a mark placed against the extents and a label written against the host's axis would name a
     position the mark is not at. */
  const ranges = useMemo(
    () => ({
      xRange: resolvedAxes.x.range,
      yRange: resolvedAxes.y.range,
      zRange: heightAxis.range,
      radiusRange: rangeOf(points, ({ radius }) => radius),
    }),
    [points, resolvedAxes, heightAxis]
  );

  /* Derived from `colorValue ?? z` rather than from `zRange`: see `toColorRange`. */
  const colorRange = useMemo(() => toColorRange(points), [points]);

  /* Destructured to fields, then memoised on them. It feeds `paint`, and `useChartCanvas`
     reschedules a repaint whenever `paint` changes identity - so `style={{ showSizeVariation: false }}`
     written inline repainted the whole chart on every parent render. */
  const {
    showSizeVariation = DEFAULT_POINTS_STYLE.showSizeVariation,
    maximumDiameter = DEFAULT_POINTS_STYLE.maximumDiameter,
    opacity = DEFAULT_POINTS_STYLE.opacity,
  } = style ?? {};

  const resolvedStyle = useMemo(
    () => ({ showSizeVariation, maximumDiameter, opacity }),
    [showSizeVariation, maximumDiameter, opacity]
  );

  /* The components the host called out, which arrive as `isContext` on each point. Highlighting
     dims everything else, and until now `highlightedComponentId` was a required argument to
     `toXyzValues` whose result no renderer read - so a host naming a context component got no
     picture back and had to discover that the flag was never the way. */
  const contextIds = useMemo(() => {
    const ids = points
      .filter(({ isContext }) => isContext)
      .map(({ uniqueId }) => uniqueId);
    return ids.length > 0 ? ids : null;
  }, [points]);

  const axisLabels = useMemo(
    () => toAxisLabels(format, NUMBER_AXIS_SCALES),
    [format]
  );
  const markListItems = useMemo(
    () =>
      toPointListItems({
        points,
        xTitle,
        yTitle,
        zTitle,
        radiusTitle,
        format,
        axisLabels,
      }),
    [points, xTitle, yTitle, zTitle, radiusTitle, format, axisLabels]
  );
  const markIds = useMemo(
    () => markListItems.map(({ id }) => id),
    [markListItems]
  );
  const pointsById = useMemo(
    () => new Map(points.map(point => [point.uniqueId, point])),
    [points]
  );
  const { activeId, setActiveId, highlightedId, containerFocusProps } =
    useMarkFocus(markIds);

  const onPointClickRef = useRef(onPointClick);
  onPointClickRef.current = onPointClick;
  const activateListedPoint = useCallback(
    (id: string) => {
      const point = pointsById.get(id);
      if (point) onPointClickRef.current?.(point);
    },
    [pointsById]
  );

  const markListColors = useMemo<MarkListColors>(
    () => ({
      surface: surfaceColor,
      ink: inkColor,
      mutedInk: inkColor,
      border: gridColor,
    }),
    [surfaceColor, inkColor, gridColor]
  );

  /* Whether a drag is open, not where its anchor is. `paint` reads only the former, and the
     latter changes on every delivered move - including the ones the pitch clamp absorbs, which
     the reducer deliberately returns a fresh anchor for so the gesture does not jump coming back
     off the limit. Depending on the object put every one of those through a full repaint:
     project every point, depth-sort, two stem passes, two paths per mark, and the axes twice, for
     a picture that did not change. `SurfaceChartController` compares the same boolean. */

  /* Memoised above the paint rather than built inside it. `paint` is rebuilt on every camera
     change, and a `ColorScale` carries a closure and a band-edge array - allocated per frame of an
     orbit for a ramp that had not moved. The other two charts get theirs from the controller,
     which holds it across frames for the same reason. */
  const resolvedLook = useMemo(() => resolvePointsLook(look), [look]);

  const colorScale = useMemo(
    () => createColorScale({ range: colorRange, ramp, bands }),
    [colorRange, ramp, bands]
  );

  const paint = useCallback(
    (
      context: CanvasRenderingContext2D,
      viewport: { width: number; height: number }
    ) => {
      const chrome = {
        context,
        camera: orbit.camera,
        viewport,
        xTitle,
        yTitle,
        zTitle,
        ...ranges,
        axisLabels,
        gridColor,
        inkColor,
        surfaceColor,
        xTickCount,
        yTickCount,
        zTickCount: heightAxis.tickCount,
        xPadding: resolvedAxes.x.padding,
        yPadding: resolvedAxes.y.padding,
        /* The tick labels and the vertical axis belong to the box, so `renderAxes3d` leaves both
           alone - without these two it strokes the corner vertical and the floor perimeter a
           second time, over the box's own. */
        boxDepth: axisBoxBase(AXIS_BOX_THICKNESS, orbit.camera.heightScale),
        hasAxisBox: true,
      };

      const box = {
        context,
        camera: orbit.camera,
        viewport,
        inkColor,
        panelColor,
        /* Off the chrome rather than restated, as `surfaceChartController` takes them: the wall's
           height rules and the labels beside them are the same divisions, and two literals are
           two chances to divide the axis differently from the numbers naming it. */
        xTickCount: chrome.xTickCount,
        yTickCount: chrome.yTickCount,
        zTickCount: chrome.zTickCount,
        xTickInset: toTickInset(resolvedAxes.x),
        yTickInset: toTickInset(resolvedAxes.y),
      };

      /* The same order `surfaceChartController` paints in: the box's faces, the floor grid over
         them, then the wall and plinth edges, then the data, then the plinth's top edge and the
         labels. One canvas here where that has three, which changes how the passes composite but
         not what order they belong in. */
      renderAxisBox({ ...box, pass: 'planes' });
      renderAxes3d({ ...chrome, pass: 'plane' });
      renderAxisBox({ ...box, pass: 'wallThickness' });
      renderAxisBox({ ...box, pass: 'plinthBase' });

      renderPoints3d({
        context,
        points,
        camera: orbit.camera,
        viewport,
        colorScale,
        style: resolvedStyle,
        look: resolvedLook,
        ...ranges,
        surfaceColor,
        inkColor,
        /* Two channels, because the two states last for different lengths of time. Folding
           context into the hover set dimmed the whole chart to 14% for as long as the view was
           open, with no interaction that cleared it - a treatment written for a gesture, used as
           a resting state. */
        /* Not while a drag is in progress. `hovered` is deliberately frozen for the length of
           one - the click handler needs it to survive the press - and it also feeds the channel
           that dims everything else, so a rotation begun over a mark faded the whole chart to 14%
           until the reader let go. The value is left alone; only the suppression is gated. */
        ...toPointHighlights({
          hoveredId: hovered?.uniqueId ?? null,
          listedId: highlightedId,
          isTurning,
          contextIds,
        }),
      });

      /* Over the marks, as the surface draws it over the relief: a line that vanishes behind the
         marks it crosses cannot be followed out to the walls. Gated like the tooltip. */
      if (hovered && !isTurning) {
        renderCrosshair3d({
          context,
          camera: orbit.camera,
          viewport,
          point: toMarkUnitPoint(hovered, ranges),
          inkColor,
          surfaceColor,
        });
      }

      renderAxisBox({ ...box, pass: 'plinthThickness' });
      renderAxes3d({ ...chrome, pass: 'heightLabels' });
      renderAxes3d({ ...chrome, pass: 'labels' });
    },
    [
      points,
      ranges,
      colorScale,
      orbit.camera,
      resolvedStyle,
      resolvedLook,
      xTitle,
      yTitle,
      zTitle,
      axisLabels,
      gridColor,
      inkColor,
      surfaceColor,
      panelColor,
      xTickCount,
      yTickCount,
      resolvedAxes,
      heightAxis.tickCount,
      hovered,
      isTurning,
      contextIds,
      highlightedId,
    ]
  );

  /* Memoised because both predicates walk the dataset, and this sits in the JSX - so it ran on
     every render, which during a rotation is every delivered `pointermove`. The paint is coalesced
     to one per frame and this was not. */

  const chartDescription = useMemo(
    () =>
      formatAccessibleName({
        pointCount: points.length,
        isBubble: resolvedStyle.showSizeVariation,
        channels: toChannels(
          {
            pointCount: points.length,
            isBubble: resolvedStyle.showSizeVariation,
            xTitle,
            yTitle,
            zTitle,
            radiusTitle,
            colorTitle,
            hasSizeField: hasSizeField(points),
            hasColorField: hasColorField(points),
          },
          channelLabels
        ),
      }),
    [
      points,
      resolvedStyle.showSizeVariation,
      xTitle,
      yTitle,
      zTitle,
      radiusTitle,
      colorTitle,
      formatAccessibleName,
      channelLabels,
    ]
  );

  const { hasPainted } = useChartCanvas(paint, canvasRef, viewport);

  const markHitCache = useRef<MarkHitCache | null>(null);
  const findMarkUnder = (cursor: {
    x: number;
    y: number;
  }): XyzDataPoint | null => {
    markHitCache.current = refreshMarkHitCache(markHitCache.current, {
      points,
      camera: orbit.camera,
      viewport,
      style: resolvedStyle,
      ...ranges,
    });
    return findMarkInCache(markHitCache.current, cursor);
  };

  /* Published once, through a ref so the identity is stable: the hook reads `canvasRef` at call
     time, so it stays correct across repaints and resizes without the host re-registering. Cleared
     on unmount, so a stale hook cannot outlive the canvas. */
  const onExportCanvasAvailableRef = useRef(onExportCanvasAvailable);
  onExportCanvasAvailableRef.current = onExportCanvasAvailable;

  /* Read through a ref for the same reason the hook is published through one: the captions change
     with the field selection, and re-publishing the hook on each change would have the host
     re-register the whole view pane. */
  const exportCaptionsRef = useRef(exportCaptions);
  exportCaptionsRef.current = exportCaptions;

  useEffect(() => {
    const publish = onExportCanvasAvailableRef.current;
    publish?.(() =>
      /* Nothing painted is nothing to export, and it is not something the copy can detect: an
         unpainted canvas carries the HTML default 300x150 backing store, so it would come back as
         a blank PNG of that size rather than as the missing canvas `pngExporter` reports. */
      hasPainted()
        ? toExportCanvas({
            canvas: canvasRef.current,
            backgroundColor: surfaceColor,
            inkColor,
            captions: exportCaptionsRef.current ?? [],
          })
        : null
    );
    return () => publish?.(null);
  }, [surfaceColor, inkColor, hasPainted]);

  /* The keyboard and the wheel move the marks under a cursor that never moved, and neither has a
     pointer event to re-resolve from - so `hovered` went on naming the mark the rotation began on,
     dimming the rest of the plot to 0.15 around a mark that had moved elsewhere, until the reader
     nudged the mouse. That also covers the wheel and the zoom keys, which have the same staleness
     and no handler of their own here.

     Resolved during the render rather than in a frame of its own, which is the correction to how
     this was first written. `useChartCanvas` is called above, so its paint frame is queued first:
     a scan in a later frame meant the paint drew the new camera with the mark resolved against the
     old one - dimming every other mark around one that had moved - and the `setHovered` after it
     forced a second full repaint. Setting state here instead re-runs this component before
     anything is committed, so `paint` closes over the resolved mark and one frame does it.

     The cost is the coalescing that arrangement bought: a hit-cache rebuild now runs per camera
     change rather than per displayed frame. That is the cheaper half - each notch already caused a
     render and a paint, so this trades a 6.2 ms rebuild at 150,000 marks against a 49 ms repaint
     and a wrong frame.

     Guarded on the camera, the viewport and the style together, because `createProjector` reads
     the first two and `maximumDiameter` moves every mark and hit radius with the third. */
  if (
    /* Frozen for the whole rotation, by pointer or by keyboard. Resolving on every key repeat
       jumped the highlighted mark and the tooltip between components under a cursor that had not
       moved. Releasing the key clears it, and this then resolves once. */
    !isRotating &&
    lastCursor.current &&
    !(
      resolvedAt?.camera === orbit.camera &&
      resolvedAt.viewport === viewport &&
      resolvedAt.style === resolvedStyle
    )
  ) {
    setResolvedAt({
      camera: orbit.camera,
      viewport,
      style: resolvedStyle,
    });
    setHovered(findMarkUnder(lastCursor.current));
  }

  return (
    <div
      ref={containerRef}
      {...containerFocusProps}
      style={{
        width: '100%',
        height: '100%',
        background: surfaceColor,
        position: 'relative',
      }}
    >
      {points.length === 0 ? (
        emptyState
      ) : (
        <>
          <canvas
            ref={bindCanvas}
            {...canvasProps}
            aria-label={chartDescription}
            /* Composed onto the shared orbit handler rather than replacing it: dragging still
           rotates, and hit-testing only runs when no drag is in progress, since resolving a
           mark under a moving cursor mid-rotation is both wasted work and confusing. */
            onPointerMove={event => {
              canvasProps.onPointerMove(event);
              const cursor = toLocalPoint(event);
              /* Recorded even mid-drag, so a rotation that ends without a pointer event resolves
               against where the cursor actually is rather than where it was before the drag. */
              lastCursor.current = cursor;
              if (orbit.drag) return;
              setTooltipAt(cursor);
              setResolvedAt({
                camera: orbit.camera,
                viewport,
                style: resolvedStyle,
              });
              setHovered(findMarkUnder(cursor));
            }}
            onPointerDown={event => {
              canvasProps.onPointerDown(event);
              pressedAt.current = toLocalPoint(event);
              setActiveId(null);
            }}
            onPointerUp={event => {
              canvasProps.onPointerUp(event);
              /* The same primary-button filter the shared handler applies, which this used to run
               past: a secondary release while the primary is still held delivers a `pointerup`
               here, and the scan below then re-resolves mid-rotation - a React re-render and a
               repaint as well as the whole-dataset hit test, for a release that ended nothing. */
              if (event.button !== 0) return;
              /* Re-resolved at the release position, because `hovered` was frozen for the length of
               the drag and the plot has turned under it. Without this the dim and the tooltip both
               come back at `pointerup` naming the mark the gesture started on, and nothing clears
               them until the pointer moves a pixel. A press that did not travel resolves to the
               same mark, so the click path below is unaffected. */
              const release = toLocalPoint(event);
              lastCursor.current = release;
              /* Claimed here as well, because this handler scans. Left unclaimed, the effect ran on
               the release - `orbit.drag` changed and the camera has moved since the last scan -
               and repeated the whole-dataset hit test this line is about to do. */
              setResolvedAt({
                camera: orbit.camera,
                viewport,
                style: resolvedStyle,
              });
              setHovered(findMarkUnder(release));
              setTooltipAt(release);
            }}
            onPointerCancel={event => {
              canvasProps.onPointerCancel(event);
              /* Cleared rather than re-resolved. `pointercancel` fires when the browser has taken
               the pointer over for a gesture of its own, so there is no cursor position left to
               resolve against - and this is the one terminator that exists because `pointerup`
               never arrives, so without it the dim and the tooltip come back naming the mark the
               rotation began on. */
              lastCursor.current = null;
              setHovered(null);
              setTooltipAt(null);
            }}
            onPointerLeave={() => {
              lastCursor.current = null;
              setHovered(null);
              setTooltipAt(null);
            }}
            onClick={event => {
              /* A click is a press that did not travel. The browser fires one after every
               `pointerup`, and `hovered` is deliberately frozen for the length of a drag - so
               without this, every orbit that began over a mark ends by selecting it, and
               `onPointClick` is the only selection hook a host has. */
              const pressed = pressedAt.current;
              pressedAt.current = null;
              if (!pressed || !hovered) return;
              const released = toLocalPoint(event);
              const travelled = Math.hypot(
                released.x - pressed.x,
                released.y - pressed.y
              );
              if (travelled > CLICK_TRAVEL_LIMIT) return;
              onPointClick?.(hovered);
            }}
          />
          <MarkList
            kind="list"
            label={markListLabel}
            items={markListItems}
            activeId={activeId}
            onActiveChange={setActiveId}
            onActivate={activateListedPoint}
            colors={markListColors}
          />
        </>
      )}
      {/* Hidden during a drag for the same reason: the frozen value names a component the
          pointer left several frames ago, whose mark has since moved across the plot. */}
      {hovered && !isTurning && (
        <div
          style={{
            position: 'absolute',
            /* Offset from the cursor rather than pinned to the corner, and offset rather than
               centred so the box never sits under the pointer it is describing. The transform
               flips it off whichever edge it would cross, without measuring it. */
            left: (tooltipAt?.x ?? 12) + 14,
            top: (tooltipAt?.y ?? 12) + 14,
            maxWidth: 'min(320px, 60%)',
            transform: tooltipFlipTransform({ at: tooltipAt, viewport }),
            padding: '6px 10px',
            borderRadius: 4,
            background: surfaceColor,
            color: inkColor,
            border: `1px solid ${gridColor}`,
            font: '12px system-ui, -apple-system, "Segoe UI", sans-serif',
            pointerEvents: 'none',
          }}
        >
          <strong>{hovered.label}</strong>
          <div>
            {xTitle}: {format(hovered.x)}
          </div>
          <div>
            {yTitle}: {format(hovered.y)}
          </div>
          <div>
            {zTitle}: {format(hovered.z)}
          </div>
          {/* Only where there is a size channel to read. This is the only per-mark readout either
              chart has, and on the bubble the area is an encoding the axes cannot show. */}
          {radiusTitle && hovered.radius !== null && (
            <div>
              {radiusTitle}: {format(hovered.radius)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
